"use client";

import { useConnection } from "wagmi";

import { AddToWalletButton } from "@/components/AddToWalletButton";
import { TokenAvatar } from "@/components/TokenAvatar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useTokenBalances } from "@/hooks/useTokenBalances";
import { useTokenList } from "@/hooks/useTokenList";
import { addressUrl, formatAmount, shortAddress } from "@/lib/format";

export function TokensPanel() {
  const { data: tokens = [], isLoading } = useTokenList();
  const { isConnected } = useConnection();
  const { balances } = useTokenBalances(tokens);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Add tokens to wallet</CardTitle>
      </CardHeader>
      <CardContent>
        {/* Unlike Pools/Positions (paginated to 3 items), this list has no
            cap — it renders every known token and will only grow as more get
            added. Left unbounded, the whole page grows with it, so this tab
            gets taller than every other one by a wide and widening margin.
            Scrolling internally past a fixed height keeps the tab's size in
            the same ballpark as the rest regardless of token count. */}
        <div className="max-h-[360px] space-y-2 overflow-y-auto pr-1">
          {isLoading ? (
            <>
              {Array.from({ length: 4 }).map((_, i) => (
                <Card key={i} className="bg-muted">
                  <CardContent className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <Skeleton className="size-7 shrink-0 rounded-full" />
                      <div className="space-y-1.5">
                        <Skeleton className="h-4 w-16" />
                        <Skeleton className="h-3 w-24" />
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Skeleton className="h-4 w-20" />
                      <Skeleton className="h-8 w-16 rounded-md" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </>
          ) : tokens.length === 0 ? (
            <p className="text-sm text-muted-foreground">No tokens found.</p>
          ) : (
            tokens.map((t, i) => {
              const raw = balances.get(t.address.toLowerCase());
              return (
                <Card
                  key={t.address}
                  className="animate-in bg-muted fade-in-0 slide-in-from-bottom-2 fill-mode-both duration-300 ease-out"
                  style={{ animationDelay: `${Math.min(i, 6) * 60}ms` }}
                >
                  <CardContent className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <TokenAvatar symbol={t.symbol} address={t.address} size={28} />
                      <div>
                        <div className="text-sm font-medium">{t.symbol}</div>
                        <div className="text-xs text-muted-foreground">{t.name}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <a
                        href={addressUrl(t.address)}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs text-muted-foreground hover:text-foreground"
                      >
                        {shortAddress(t.address)} ↗
                      </a>
                      {isConnected && raw !== undefined && raw > 0n && (
                        <span className="text-xs text-muted-foreground">{formatAmount(raw, t.decimals)}</span>
                      )}
                      <AddToWalletButton
                        asset={{ type: "ERC20", address: t.address, symbol: t.symbol, decimals: t.decimals }}
                        assetLabel={t.symbol}
                      />
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      </CardContent>
    </Card>
  );
}
